import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FiltersBar, Input, type FilterDef } from '../../../ui/index.js';
import type { CargoProblemFilterOptions } from '../../../api/cargo-problems.js';
import {
  CARGO_PROBLEM_CATEGORIES,
  CARGO_PROBLEM_QUEUES,
  CARGO_PROBLEM_STATUSES,
  useCargoProblemLabels,
} from '../labels.js';
import type { CargoProblemsFiltersState } from '../filters.js';

export function CargoProblemsFilters({
  value,
  options,
  onChange,
}: {
  value: CargoProblemsFiltersState;
  options?: CargoProblemFilterOptions;
  onChange: (next: CargoProblemsFiltersState) => void;
}) {
  const { t } = useTranslation('cargoProblems');
  const labels = useCargoProblemLabels();
  const actorLabels = useRef(new Map<string, string>());
  const defs = useMemo<FilterDef<CargoProblemsFiltersState>[]>(() => {
    const authors = preserveActors(options?.authors ?? [], value.authorId, actorLabels.current);
    const assignees = preserveActors(
      options?.assignees ?? [],
      value.assigneeId,
      actorLabels.current,
    );
    const carriers = preserveCarriers(options?.carriers ?? [], value.carrier);
    return [
      {
        type: 'search',
        key: 'q',
        label: t('filters.search'),
        placeholder: t('filters.searchPlaceholder'),
      },
      {
        type: 'select',
        key: 'status',
        label: t('filters.status'),
        options: CARGO_PROBLEM_STATUSES.map((item) => ({
          value: item,
          label: labels.status(item),
        })),
      },
      {
        type: 'select',
        key: 'category',
        label: t('filters.category'),
        options: CARGO_PROBLEM_CATEGORIES.map((item) => ({
          value: item,
          label: labels.category(item),
        })),
      },
      {
        type: 'select',
        key: 'queue',
        label: t('filters.queue'),
        options: CARGO_PROBLEM_QUEUES.map((item) => ({ value: item, label: labels.queue(item) })),
      },
      {
        type: 'select',
        key: 'authorId',
        label: t('filters.author'),
        options: authors,
      },
      {
        type: 'select',
        key: 'assigneeId',
        label: t('filters.assignee'),
        options: assignees,
      },
      {
        type: 'select',
        key: 'carrier',
        label: t('filters.carrier'),
        options: carriers,
      },
    ];
  }, [labels, options, t, value.assigneeId, value.authorId, value.carrier]);
  return (
    <div className="cargo-problems-filters">
      <FiltersBar filters={defs} value={value} onChange={onChange} />
      <div className="cargo-problems-date-filters">
        <DateFilter
          label={t('filters.createdFrom')}
          value={value.createdFrom}
          onChange={(createdFrom) => onChange({ ...value, createdFrom })}
        />
        <DateFilter
          label={t('filters.createdTo')}
          value={value.createdTo}
          onChange={(createdTo) => onChange({ ...value, createdTo })}
        />
        <DateFilter
          label={t('filters.updatedFrom')}
          value={value.updatedFrom}
          onChange={(updatedFrom) => onChange({ ...value, updatedFrom })}
        />
        <DateFilter
          label={t('filters.updatedTo')}
          value={value.updatedTo}
          onChange={(updatedTo) => onChange({ ...value, updatedTo })}
        />
        <label className="cargo-problems-check">
          <input
            type="checkbox"
            checked={value.overdueOnly}
            onChange={(event) => onChange({ ...value, overdueOnly: event.target.checked })}
          />
          {t('filters.overdueOnly')}
        </label>
      </div>
    </div>
  );
}

function preserveActors(
  items: readonly { id: string; name: string }[],
  selected: string,
  knownLabels: Map<string, string>,
) {
  for (const item of items) knownLabels.set(item.id, item.name);
  const result = items.map((item) => ({ value: item.id, label: item.name }));
  if (selected && !items.some((item) => item.id === selected)) {
    result.unshift({ value: selected, label: knownLabels.get(selected) ?? selected });
  }
  return result;
}

function preserveCarriers(items: readonly string[], selected: string) {
  const result = items.map((item) => ({ value: item, label: item }));
  if (selected && !items.includes(selected)) result.unshift({ value: selected, label: selected });
  return result;
}

function DateFilter({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label>
      <span>{label}</span>
      <Input type="date" value={value} onChange={onChange} />
    </label>
  );
}
